import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { useAuth } from "../auth";
import { getMe } from "./me-api";
import type { MeResponse } from "./user-doc";

interface MeState {
  me: MeResponse | null;
  loading: boolean;
  error: string | null;
  refresh(): Promise<void>;
}

const Ctx = createContext<MeState>({
  me: null,
  loading: true,
  error: null,
  refresh: async () => undefined,
});

export function MeProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    if (!user) {
      setMe(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setMe(await getMe());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    void refresh();
  }, [authLoading, refresh]);

  return <Ctx.Provider value={{ me, loading, error, refresh }}>{children}</Ctx.Provider>;
}

export function useMe(): MeState {
  return useContext(Ctx);
}

/** Convenience selector — true iff me has loaded AND tier === "free". */
export function useIsFreeTier(): boolean {
  const { me } = useMe();
  return me?.tier === "free";
}
